import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Form, Input, DatePicker, TimePicker, Select, Switch, Button, Message, Upload } from '@arco-design/web-react';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';
import { getAuth } from 'firebase/auth';
import dayjs, { Dayjs } from 'dayjs';
import { Event, CreateEventRequest } from '../../models/Event';
import axios from 'axios';
import { endpoints } from '../../api/endpoints';
import { EVENT_IMAGE_TYPES } from '../../constants/imageTypes';
import { cachedToken } from '../../setupAxios';
import classNames from 'classnames';
import { currentThemeName } from '../../theme';
import {
  buildEventDatePayload,
  DEFAULT_EVENT_TIMEZONE,
  getPreferredEventTimezoneOptions,
  parseEventForForm,
} from '../../utils/eventDateTime';
import { EventImageCropDialog } from '../EventImageCropDialog/EventImageCropDialog';
import './EventForm.css';

const EVENT_FORM_ID = 'event-form';

interface EventFormProps {
  visible: boolean;
  event?: Event | null;
  onClose: () => void;
  onSuccess: () => void;
}

const FormItem = Form.Item;
const TextArea = Input.TextArea;

export const EventForm: React.FC<EventFormProps> = ({ visible, event, onClose, onSuccess }) => {
  const { t } = useTranslation();
  const isCompactViewport = useMediaQuery({ maxWidth: 1023 });
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [imageUrl, setImageUrl] = useState<string>('');
  const [imageUploading, setImageUploading] = useState(false);
  const [idToken, setIdToken] = useState<string>('');
  const [fileList, setFileList] = useState<any[]>([]);
  const [cropImageFile, setCropImageFile] = useState<File | null>(null);
  const [showImageCropDialog, setShowImageCropDialog] = useState(false);
  const timezoneOptions = useMemo(() => getPreferredEventTimezoneOptions(), []);
  const currentUser = getAuth().currentUser;

  useEffect(() => {
    const getToken = async () => {
      try {
        const token = cachedToken ?? await currentUser?.getIdToken();
        if (token) {
          setIdToken(token);
        }
      } catch (error) {
        console.error('Error getting ID token:', error);
      }
    };
    getToken();
  }, [currentUser]);

  useEffect(() => {
    if (visible && event) {
      setImageUploading(false);
      const parsed = parseEventForForm(event.event_date, event.timezone);
      form.setFieldsValue({
        title: event.title,
        description: event.description,
        event_date: parsed.eventDate,
        event_time: parsed.eventTime,
        timezone: parsed.timezone,
        location: event.location,
        rsvp_url: event.rsvp_url,
        organizer: event.organizer || '',
        is_pinned: event.is_pinned,
        is_published: event.is_published,
        send_sms_notification: false,
      });
      const url = event.image_url || '';
      setImageUrl(url);
      setFileList(url ? [{
        uid: '-1',
        url: url,
        name: 'event-image',
        status: 'done',
      }] : []);
    } else if (visible) {
      setImageUploading(false);
      form.resetFields();
      form.setFieldsValue({
        event_date: dayjs(),
        event_time: dayjs().hour(9).minute(0).second(0),
        timezone: DEFAULT_EVENT_TIMEZONE,
        is_pinned: false,
        is_published: true,
        send_sms_notification: false,
      });
      setImageUrl('');
      setFileList([]);
    }
  }, [visible, event, form]);

  const handleSubmit = async () => {
    if (imageUploading) {
      Message.warning(t('notification_messages.image_upload_wait'));
      return;
    }

    try {
      const values = await form.validate();
      let event_date: string;
      let timezone: string;
      try {
        ({ event_date, timezone } = buildEventDatePayload(
          values.event_date,
          values.event_time,
          values.timezone
        ));
      } catch (parseError) {
        console.error('Event date/time parse failed:', parseError, values.event_date, values.event_time);
        Message.error(t('form_validation_messages.required_field'));
        return;
      }

      const eventData: CreateEventRequest = {
        title: values.title,
        description: values.description,
        event_date,
        timezone,
        location: values.location,
        rsvp_url: values.rsvp_url,
        image_url: imageUrl || undefined,
        organizer: values.organizer || undefined,
        is_pinned: values.is_pinned || false,
        is_published: values.is_published ?? true,
        send_sms_notification: values.send_sms_notification || false,
      };

      setLoading(true);

      if (event) {
        await axios.put(endpoints.admin.updateEvent(event.id), eventData);
        Message.success(t('notification_messages.event_updated_success'));
      } else {
        await axios.post(endpoints.admin.createEvent, eventData);
        Message.success(t('notification_messages.event_created_success'));
      }

      onSuccess();
      onClose();
      form.resetFields();
      setImageUrl('');
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const backendMessage = error.response?.data;
        Message.error(
          typeof backendMessage === 'string' && backendMessage
            ? backendMessage
            : (event
              ? t('notification_messages.event_update_error')
              : t('notification_messages.event_create_error'))
        );
      } else {
        console.error('Event form submit failed:', error);
        Message.error(t('form_validation_messages.required_field'));
      }
    } finally {
      setLoading(false);
    }
  };

  const uploadEventImage = async (file: File) => {
    if (!idToken) {
      Message.error(t('notification_messages.file_uploaded_error'));
      return;
    }

    setImageUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post(endpoints.admin.uploadEventImage, formData, {
        headers: {
          Authorization: `Bearer ${idToken}`,
          'Content-Type': 'multipart/form-data',
        },
      });
      const url = response.data.url as string;
      setImageUrl(url);
      setFileList([{
        uid: Date.now().toString(),
        url,
        name: file.name,
        status: 'done',
      }]);
      Message.success(t('notification_messages.image_uploaded_success'));
    } catch (error) {
      console.error('Event image upload failed:', error);
      Message.error(t('notification_messages.file_uploaded_error'));
    } finally {
      setImageUploading(false);
    }
  };

  const beforeUpload = (file: File) => {
    if (!EVENT_IMAGE_TYPES.includes(file.type)) {
      Message.error(t('form_validation_messages.invalid_file_type'));
      return false;
    }
    setCropImageFile(file);
    setShowImageCropDialog(true);
    return false;
  };

  const validateRsvpUrl = (value: any, callback: any) => {
    if (!value || !value.trim()) {
      return callback();
    }
    const urlRegex = /^(https?:\/\/)?([\w-]+\.)+[\w-]+(\/[\w- .\/?%&=]*)?$/;
    if (!urlRegex.test(value.trim())) {
      return callback(t('form_validation_messages.invalid_url'));
    }
    callback();
  };

  return (
    <>
    <Modal
      title={event ? t('events.edit_event') : t('events.create_event')}
      visible={visible}
      onCancel={onClose}
      alignCenter={!isCompactViewport}
      wrapClassName={classNames('event-form-modal-wrapper', {
        'event-form-modal-wrapper--black-theme': currentThemeName === 'black',
        'event-form-modal-wrapper--compact': isCompactViewport,
      })}
      className={classNames('event-form-modal', {
        'event-form-modal--black-theme': currentThemeName === 'black',
      })}
      footer={
        <div className="event-form__actions">
          <Button
            onClick={onClose}
            className="event-form__button event-form__button--secondary"
          >
            {t('buttons.cancel')}
          </Button>
          <Button
            type="primary"
            htmlType="submit"
            form={EVENT_FORM_ID}
            loading={loading}
            className="event-form__button event-form__button--primary"
          >
            {event ? t('buttons.update') : t('buttons.create')}
          </Button>
        </div>
      }
    >
      <Form
        id={EVENT_FORM_ID}
        form={form}
        layout="vertical"
        scrollToFirstError
        onSubmit={handleSubmit}
      >
        <FormItem
          label={t('events.title')}
          field="title"
          rules={[{ required: true, message: t('form_validation_messages.required_field') }]}
        >
          <Input placeholder={t('events.title_placeholder')} />
        </FormItem>

        <FormItem
          label={t('events.description')}
          field="description"
          rules={[{ required: true, message: t('form_validation_messages.required_field') }]}
        >
          <TextArea
            placeholder={t('events.description_placeholder')}
            rows={isCompactViewport ? 4 : 3}
            maxLength={1000}
            showWordLimit
            className="event-form__description"
          />
        </FormItem>

        <FormItem
          label={t('events.event_date')}
          field="event_date"
          rules={[{ required: true, message: t('form_validation_messages.required_field') }]}
        >
          <DatePicker
            format="MMMM D, YYYY"
            placeholder={t('events.event_date_placeholder')}
            style={{ width: '100%' }}
            onChange={(value) => {
              form.setFieldValue('event_date', value);
            }}
            onSelect={(value) => {
              form.setFieldValue('event_date', value);
            }}
          />
        </FormItem>

        <FormItem
          label={t('events.event_time')}
          field="event_time"
          rules={[{ required: true, message: t('form_validation_messages.required_field') }]}
        >
          <TimePicker
            use12Hours
            format="h:mm A"
            placeholder={t('events.event_time_placeholder')}
            style={{ width: '100%' }}
            disableConfirm
            step={{ hour: 1, minute: 15 }}
            onChange={(value) => {
              form.setFieldValue('event_time', value);
            }}
            onSelect={(value) => {
              form.setFieldValue('event_time', value);
            }}
          />
        </FormItem>

        <FormItem
          label={t('events.event_timezone')}
          field="timezone"
          rules={[{ required: true, message: t('form_validation_messages.required_field') }]}
        >
          <Select
            showSearch
            placeholder={t('events.event_timezone_placeholder')}
            options={timezoneOptions}
            style={{ width: '100%' }}
            filterOption={(input, option) => {
              const opt = option as { value?: string; label?: string };
              const haystack = `${opt.value ?? ''} ${opt.label ?? ''}`.toLowerCase();
              return haystack.includes(input.toLowerCase());
            }}
          />
        </FormItem>

        <FormItem
          label={t('events.location')}
          field="location"
          rules={[{ required: true, message: t('form_validation_messages.required_field') }]}
        >
          <Input placeholder={t('events.location_placeholder')} />
        </FormItem>

        <FormItem
          label={t('events.rsvp_url')}
          field="rsvp_url"
          rules={[{ validator: validateRsvpUrl }]}
        >
          <Input placeholder={t('events.rsvp_url_placeholder')} />
        </FormItem>

        <FormItem
          label={t('events.event_image')}
        >
          <Upload
            drag
            limit={1}
            accept={EVENT_IMAGE_TYPES.join(',')}
            tip={t('events.event_image_tip')}
            listType="picture-card"
            imagePreview
            fileList={fileList}
            onRemove={() => {
              setImageUrl('');
              setFileList([]);
              return true;
            }}
            beforeUpload={beforeUpload}
          />
        </FormItem>

        <FormItem
          label={t('events.organizer')}
          field="organizer"
        >
          <Input placeholder={t('events.organizer_placeholder')} />
        </FormItem>

        <FormItem
          label={t('events.pin_event')}
          field="is_pinned"
          triggerPropName="checked"
        >
          <Switch />
        </FormItem>

        <FormItem
          label={t('events.publish_event')}
          field="is_published"
          triggerPropName="checked"
        >
          <Switch />
        </FormItem>

        <FormItem
          label={t('events.sms_notification_to_users')}
          field="send_sms_notification"
          triggerPropName="checked"
        >
          <Switch />
        </FormItem>
      </Form>
    </Modal>

    {cropImageFile && (
      <EventImageCropDialog
        visible={showImageCropDialog}
        file={cropImageFile}
        onCancel={() => {
          setShowImageCropDialog(false);
          setCropImageFile(null);
        }}
        onConfirm={async (file) => {
          setShowImageCropDialog(false);
          setCropImageFile(null);
          await uploadEventImage(file);
        }}
      />
    )}
    </>
  );
};
